import { useEffect, useState, useRef } from "react";
import { NavBar } from "../components/navigation";
import axios from "axios";
import { ASSETS_URL, BASE_URL, COLORS } from "../constants";
import { Modal } from "../components/modals";
import { useFormik } from "formik";
import { CompanyType } from "../components/types";
import * as Yup from "yup";
import clsx from "clsx";
import {
  BallsLoader,
  LengthyLoader,
  SpinnerLg,
  SpinnerSm,
  TubeLoader,
} from "../components/loaders";
import { Footer } from "../components/footer";
import { Identification } from "../components/Identification";
import { NavLink } from "react-router-dom";

export type Identity = {
  company_id: number;
  identity_id: number;
  identity_name: string;
  identity_photo: string;
  identity_position: string;
  identity_reference: string;
  identity_status: string;
  identity_uuid: string;
};
type IdentityItem = Identity & CompanyType;
export const Identities = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [image, setImage] = useState<any>();
  const [imageError, setImageError] = useState<any>();
  const [identities, setIdentities] = useState<IdentityItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const removeModal = useRef<HTMLButtonElement>(null);
  const [edit, setEdit] = useState<Identity | undefined>();
  let validation = Yup.object({
    identity_name: Yup.string()
      .min(4, "Name should be atleast 4 charachers")
      .required("Name is required"),
    identity_position: Yup.string()
      .min(2, "Position should be atleast 2 charachers")
      .required("Position is required"),
    identity_reference: Yup.string().required("Reference number is required"),
  });

  const getIdenties = async () => {
    setIsLoading(true);
    try {
      let results = await axios.get(`${BASE_URL}/identities`);
      console.log(results);
      if (results.data.status) {
        setIdentities(results.data.identities);
      }
    } catch (ex) {
      console.log(ex);
    } finally {
      setIsLoading(false);
    }
  };

  const replaceIdentity = (id: IdentityItem) => {
    setIdentities(
      identities.map((idd) => {
        if (idd.identity_id !== id.identity_id) {
          return idd;
        }
        return id;
      })
    );
  };

  const onSubmit = async (e: any) => {
    //PERFORM VALIDATION FOR THE IMAGE
    setIsCreating(true);
    let formData = new FormData();
    formData.append("identity_name", e.identity_name);
    formData.append("identity_position", e.identity_position);
    formData.append("identity_reference", e.identity_reference);
    formData.append("identity_status", e.identity_status);
    formData.append("identity_photo", image);
    setImageError(undefined);
    try {
      let results = edit
        ? await axios.post(
            `${BASE_URL}/identities/update/${edit.identity_id}`,
            formData
          )
        : await axios.post(`${BASE_URL}/identities/add`, formData);

      if (edit) {
        if (results.data.status) {
          replaceIdentity(results.data.identity);
        }
      } else {
        //add to a row
        if (results.data.status) {
          setIdentities([results.data.identity, ...identities]);
        }
      }
      if (removeModal.current) {
        removeModal.current.click();
      }
      formik.resetForm();
    } catch (ex: any) {
      console.log(ex);
      if (ex.response && ex.response.data && ex.response.data.message) {
        setImageError(ex.response.data.message);
      } else {
        setImageError("Please provide a valid user image");
      }
    } finally {
      setIsCreating(false);
    }
  };
  const formik = useFormik({
    initialValues: {
      identity_name: edit ? edit.identity_name : "",
      identity_position: edit ? edit.identity_position : "",
      identity_reference: edit ? edit.identity_reference : "",
      identity_status: edit ? edit.identity_status : "Valid",
    },
    onSubmit,
    validationSchema: validation,
    enableReinitialize: true,
  });

  //we shouldnt forget this trick for tsx
  const selectImage = () =>
    inputRef.current ? inputRef.current.click() : null;

  const handleImageChange = (event: any) => {
    const file = event.target.files[0];
    setImage(file);
  };
  useEffect(() => {
    getIdenties();
  }, []);

  return (
    <>
      <NavBar data={undefined} />

      {!isLoading ? (
        <>
          <Modal title="New Identity" modalRef={removeModal}>
            {/* START OF IDENTITY CREATION FORM */}

            <form className="row g-3">
              <div className="col-12">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <input
                  type="text"
                  className={clsx("form-control", {
                    "is-invalid":
                      formik.errors.identity_name &&
                      formik.touched.identity_name,
                  })}
                  id="name"
                  value={formik.values.identity_name}
                  onChange={formik.handleChange("identity_name")}
                  onBlur={formik.handleBlur("identity_name")}
                  placeholder="Scolastica Msokwa"
                  autoComplete="off"
                />
                {formik.errors.identity_name && formik.touched.identity_name ? (
                  <div className="invalid-feedback">
                    {formik.errors.identity_name}
                  </div>
                ) : null}
              </div>
              <div className="col-md-5">
                <label htmlFor="inputCity" className="form-label">
                  Reference
                </label>
                <input
                  type="text"
                  className={clsx("form-control", {
                    "is-invalid":
                      formik.errors.identity_reference &&
                      formik.touched.identity_reference,
                  })}
                  value={formik.values.identity_reference}
                  onChange={formik.handleChange("identity_reference")}
                  onBlur={formik.handleBlur("identity_reference")}
                  autoComplete="off"
                  placeholder="AF/003"
                  id="inputCity"
                />
                {formik.errors.identity_reference &&
                formik.touched.identity_reference ? (
                  <div className="invalid-feedback">
                    {formik.errors.identity_reference}
                  </div>
                ) : null}
              </div>

              <div className="col-md-4">
                <label htmlFor="position" className="form-label">
                  Position
                </label>
                <input
                  type="text"
                  className={clsx("form-control", {
                    "is-invalid":
                      formik.errors.identity_position &&
                      formik.touched.identity_position,
                  })}
                  placeholder="SD Manager"
                  id="position"
                  onChange={formik.handleChange("identity_position")}
                  onBlur={formik.handleBlur("identity_position")}
                  value={formik.values.identity_position}
                />
                {formik.errors.identity_position &&
                formik.touched.identity_position ? (
                  <div className="invalid-feedback">
                    {formik.errors.identity_position}
                  </div>
                ) : null}
              </div>
              <div className="col-md-3 ">
                <label htmlFor="status" className="form-label">
                  Status
                </label>
                <select
                  id="status"
                  className={clsx("form-control", {
                    "is-invalid":
                      formik.errors.identity_status &&
                      formik.touched.identity_status,
                  })}
                  onChange={formik.handleChange("identity_status")}
                  onBlur={formik.handleBlur("identity_status")}
                  value={formik.values.identity_status}
                >
                  <option selected value="Valid">
                    Valid
                  </option>
                  <option value="Expired">Expired</option>
                </select>
                {formik.errors.identity_status &&
                formik.touched.identity_status ? (
                  <div className="invalid-feedback">
                    {formik.errors.identity_status}
                  </div>
                ) : null}
              </div>
              <div>
                {image ? (
                  <img
                    onClick={selectImage}
                    src={
                      edit
                        ? typeof image === "string"
                          ? `${ASSETS_URL}/storage/images/${image}`
                          : URL.createObjectURL(image)
                        : URL.createObjectURL(image)
                    }
                    width="150px"
                    style={{ cursor: "pointer" }}
                    alt="User Stamp size"
                  />
                ) : (
                  <img
                    onClick={selectImage}
                    src={require("../assets/user.jpg")}
                    width="150px"
                    style={{ cursor: "pointer" }}
                    alt="User Stamp size"
                  />
                )}
                <input
                  type="file"
                  ref={inputRef}
                  accept="image/*"
                  onChange={(e) => {
                    handleImageChange(e);
                  }}
                  style={{ display: "none" }}
                />
              </div>
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-warning"
                  onClick={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                  }}
                >
                  {isCreating ? (edit ? "Updating..." : "Saving...") : "Save"}
                </button>
                {imageError ? (
                  <p className="text-danger">{imageError}</p>
                ) : null}
              </div>
            </form>

            {/* END OF IDENTITY CREATION FORM */}
          </Modal>
          <div className="vh-100 d-flex flex-column">
            <div className="container mt-5 flex-grow-1">
              <div className="row">
                <div
                  className={`col-12 d-flex 
             justify-content-between 
             align-items-center  
             py-3 px-1 mb-4 `}
                >
                  <Identification />
                  <button
                    className="btn text-light"
                    style={{ backgroundColor: COLORS.ORANGE }}
                    data-bs-toggle="modal"
                    data-bs-target="#exampleModal"
                    onClick={() => {
                      setEdit(undefined);
                      setImage(undefined);
                    }}
                  >
                    New Identity
                  </button>
                </div>
              </div>
              <div className="row shadow rounded bg-light">
                <div className="col-12 p-3">
                  <table className="table table-hover">
                    <thead>
                      <tr className="table-war">
                        <th scope="col">#</th>
                        <th scope="col">ID</th>
                        <th scope="col">Reference</th>
                        <th scope="col">Name</th>
                        <th scope="col">Position</th>
                        <th scope="col">Photo</th>
                        <th scope="col">Status</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody className="table-light">
                      {identities.map((id: IdentityItem, idx) => (
                        <tr key={id.identity_id}>
                          <th scope="row">{idx + 1}</th>
                          <td>{id.identity_uuid}</td>
                          <td>{id.identity_reference}</td>
                          <td>{id.identity_name}</td>
                          <td>{id.identity_position}</td>
                          <td>
                            <img
                              src={`${ASSETS_URL}/storage/images/${id.identity_photo}`}
                              alt={id.identity_photo}
                              width="60"
                            />
                          </td>
                          <td>
                            <span
                              className="d-inline-block px-2 text-uppercase fw-bolder"
                              style={{
                                fontSize: 0.7 + "em",
                                padding: 1 + "px",
                                borderRadius: 4 + "px",
                                backgroundColor:
                                  id.identity_status === "Expired"
                                    ? COLORS.GRAY
                                    : COLORS.PALE_GREEN,

                                color:
                                  id.identity_status === "Expired"
                                    ? "white"
                                    : COLORS.GREEN,
                              }}
                            >
                              {id.identity_status}
                            </span>
                          </td>
                          <td
                            className="align-center justify-space-center"
                            style={{ height: 100 + "%" }}
                          >
                            <div className="d-flex align-items-center">
                              <i
                                className="bi bi-pencil-fill text-warning"
                                data-bs-toggle="modal"
                                data-bs-target="#exampleModal"
                                onClick={() => {
                                  setImage(id.identity_photo);
                                  setEdit(id);
                                }}
                                style={{ fontSize: 15, cursor: "pointer" }}
                              ></i>
                              <NavLink to={`/${id.identity_uuid}`}>
                                <i
                                  className="bi bi-link-45deg mx-1 text-info"
                                  style={{ fontSize: 19, cursor: "pointer" }}
                                ></i>
                              </NavLink>
                              {/* Dropend button started */}
                              <div className="btn-group dropend d-inline-block">
                                <i
                                  className="bi bi-qr-code-scan"
                                  style={{ cursor: "pointer" }}
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                ></i>
                                <ul className="dropdown-menu">
                                  <li>
                                    <a
                                      className="dropdown-item"
                                      href={`https://api.qrserver.com/v1/create-qr-code/?data=https://${id.company_domain}/${id.identity_uuid}&size=100x100`}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Size 100x100
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      className="dropdown-item"
                                      href={`https://api.qrserver.com/v1/create-qr-code/?data=https://${id.company_domain}/${id.identity_uuid}&size=250x250`}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Size 250x250
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      className="dropdown-item"
                                      href={`https://api.qrserver.com/v1/create-qr-code/?data=https://${id.company_domain}/${id.identity_uuid}&size=500x500`}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Size 500x500
                                    </a>
                                  </li>
                                  <li>
                                    <a
                                      className="dropdown-item"
                                      href={`https://api.qrserver.com/v1/create-qr-code/?data=https://${id.company_domain}/${id.identity_uuid}&size=800x800`}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Size 800x800
                                    </a>
                                  </li>
                                </ul>
                              </div>
                            </div>
                            {/* Dropend button ended */}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </>
      ) : (
        <div className="d-flex justify-content-center align-items-center vw-100 vh-100">
          <TubeLoader />
        </div>
      )}
    </>
  );
};
